// 모바일 상태 배너 (모바일 UX Phase 1, 2026-09-21).
// 섹션 최상단에 "지금 상태"를 한 줄로 요약해 운영자가 5초 안에 정상/확인/조치를 판단하게 한다.
//
// 원칙:
//   · deterministic 입력만 사용 — 원인 추정/LLM 금지. 호출부가 이미 계산한 severity + 사실 문구만.
//   · severity(색)와 도메인(의미)을 분리한다. 색은 심각도 표현일 뿐, 문구는 도메인 언어를 쓴다.
//       - revenue : 손익/매출 언어(적자·비용 증가·매출 감소). "장애"처럼 쓰지 않는다.
//       - finops  : 비용/마진 언어.
//       - system  : 실제 시스템 오류/timeout/throttle 언어.
//   · 정상(ok)일 때 불필요한 경고 문구를 만들지 않는다.
//
// props:
//   level: "ok" | "info" | "warn" | "critical"
//   messages: string[]  (level != ok 일 때 표시할 deterministic 사실 문구. 없으면 생략)
//   domain: "revenue" | "finops" | "system"  (기본 문안 톤 결정)
//   headline: 기본 문안 override (기존 화면 문구를 그대로 유지해야 할 때 — 회귀 방지용)
//   hint: 헤드라인 옆 회색 보조 문구(선택)
import clsx from "clsx";

const LEVEL_STYLE = {
  ok:       { cls: "border-emerald-200 bg-emerald-50", dot: "bg-emerald-500" },
  info:     { cls: "border-sky-200 bg-sky-50",         dot: "bg-sky-500" },
  warn:     { cls: "border-amber-200 bg-amber-50",     dot: "bg-amber-500" },
  critical: { cls: "border-red-200 bg-red-50",         dot: "bg-red-500" },
};

// 도메인별 기본 헤드라인 — 색이 아니라 "말"로 도메인을 구분(요구사항 3).
const HEADLINE = {
  revenue: {
    ok: "손익 정상 — 현재 특이 사항 없음",
    info: "손익 참고 사항",
    warn: "손익 확인 필요",
    critical: "손익 조치 필요",
  },
  finops: {
    ok: "비용·마진 정상 — 현재 특이 사항 없음",
    info: "비용·마진 참고 사항",
    warn: "비용·마진 확인 필요",
    critical: "비용·마진 조치 필요",
  },
  system: {
    ok: "시스템 정상 — 오류·지연 특이 사항 없음",
    info: "시스템 참고 사항",
    warn: "시스템 확인 필요",
    critical: "시스템 조치 필요",
  },
};

// 순수 함수로 분리(node 환경 테스트로 계약 고정).
// 도메인별 문구를 반환 — 색(severity)과 의미(domain)를 섞지 않는다.
export function bannerHeadline(domain, level) {
  const table = HEADLINE[domain] || HEADLINE.system;
  return table[level] || table.ok;
}

// ok 면 사실 문구를 만들지 않는다(정상 상태에서 불필요한 경고 금지).
export function visibleFacts(level, messages) {
  if (level === "ok") return [];
  return (messages || []).filter(Boolean);
}

export default function MobileStatusBanner({
  level = "ok", messages = [], domain = "system", headline, hint, className,
}) {
  const lv = LEVEL_STYLE[level] || LEVEL_STYLE.ok;
  const title = headline || bannerHeadline(domain, level);
  const facts = visibleFacts(level, messages);

  return (
    <div className={clsx("rounded-2xl border px-4 py-3.5", lv.cls, className)}>
      <p className="flex items-center gap-2 text-[14px] font-semibold text-ink-800">
        <span className={clsx("h-2.5 w-2.5 shrink-0 rounded-full", lv.dot)} />
        {title}
        {hint && <span className="ml-1 font-normal text-ink-400 text-[12px]">{hint}</span>}
      </p>
      {facts.length > 0 && (
        <ul className="mt-2 space-y-1 text-[13px] leading-snug text-ink-600">
          {facts.map((m, i) => (
            <li key={i} className="list-disc ml-4">{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// 헬퍼: alerts 배열([{level, message}]) → 최악 level + 문구 목록. (finops/revenue 공용)
// level 우선순위: critical > warn > info > ok. 원인 추정 없이 있는 사실만 모은다.
export function worstLevel(alerts) {
  const order = { critical: 3, warn: 2, info: 1, ok: 0 };
  let worst = "ok";
  for (const a of alerts || []) {
    if ((order[a.level] ?? 0) > (order[worst] ?? 0)) worst = a.level;
  }
  return worst;
}
